import { Trans, useLingui } from '@lingui/react/macro'
import dayjs from 'dayjs'
import { useScatterplotContext } from 'features/timeSeries'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useTimeSeriesService } from 'features/timeSeries/timeSeriesService'
import {
  afterProbeWindow,
  beforeProbeWindow,
  boundaryJumpWindow,
} from 'features/timeSeries/utils/dataBoundaries'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

// How far back to look for the last data before an empty window. Coarse, and a
// year is generous next to the sizes people pan around; older data than this is
// rare enough not to warrant a wider (slower) probe by default.
const BEFORE_LOOKBACK_MS = 365 * 86_400_000

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${CHART_COLORS.TEXT};
  margin-bottom: 4px;
`

const Detail = styled.div`
  font-size: 12px;
  color: ${CHART_COLORS.TOOLTIP_NAME_COLOR};
  line-height: 1.5;
  max-width: 22rem;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: center;
  /* The empty-state container is pointer-events:none so it never blocks chart
     interaction; re-enable it on the controls that need clicks. */
  pointer-events: auto;
`

const JumpButton = styled.button`
  background: color-mix(in oklab, var(--foreground) 8%, transparent);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 5px 10px;
  color: var(--foreground);
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: color-mix(in oklab, var(--foreground) 16%, transparent);
  }
`

interface Bounds {
  before: string | null
  after: string | null
}

const formatDay = (iso: string) => dayjs(iso).format('MMM D, YYYY')

/**
 * Empty-state body for a window that genuinely has no data. Rather than a dead
 * end, it probes for the nearest data on each side and offers to jump there.
 *
 * Only rendered once the range has actually settled empty (the loading gate
 * upstream owns that decision), so a probe here always describes a real gap.
 */
export const NoDataInRange: React.FC = () => {
  const { t } = useLingui()

  const { selectedSensors, timeRange, setTimeRange, exitLiveTail } =
    useScatterplotContext()
  const { fetchDataPresence } = useTimeSeriesService()

  const [bounds, setBounds] = useState<Bounds>({ before: null, after: null })
  const [probing, setProbing] = useState(false)

  // Only sensors have raw presence; a derived value is a formula over them, so
  // its data existence is its inputs'. Probing the sensor ids is sufficient.
  const sensorKey = selectedSensors.map((sensor) => sensor.id).join(',')
  // Stable identity: recompute only when the id set actually changes, so the
  // presence-probe effect below does not re-run (and re-fetch) on every render.
  const sensorIds = useMemo(
    () => selectedSensors.map((sensor) => sensor.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sensorKey]
  )
  const rangeKey = `${timeRange.from}|${timeRange.to}`

  // Keyed by (pens, range) so a stale probe cannot describe a window the user
  // has already left.
  const requestKeyRef = useRef('')

  useEffect(() => {
    const requestKey = `${sensorKey}::${rangeKey}`
    requestKeyRef.current = requestKey

    if (sensorIds.length === 0) {
      setBounds({ before: null, after: null })
      return
    }

    const fromMs = Date.parse(timeRange.from)
    const toMs = Date.parse(timeRange.to)
    if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return

    const nowMs = Date.now()
    const before = beforeProbeWindow(fromMs, BEFORE_LOOKBACK_MS)
    const after = afterProbeWindow(toMs, nowMs)

    setProbing(true)
    Promise.all([
      fetchDataPresence(sensorIds, before.start, before.end),
      after
        ? fetchDataPresence(sensorIds, after.start, after.end)
        : Promise.resolve({ earliest: null, latest: null }),
    ])
      .then(([beforeBounds, afterBounds]) => {
        if (requestKeyRef.current !== requestKey) return
        setBounds({
          before: beforeBounds.latest,
          after: afterBounds.earliest,
        })
      })
      .catch(() => {
        // Presence is a courtesy on top of an already-empty chart; a failed
        // probe just means no jump affordance, never an error state.
        if (requestKeyRef.current === requestKey) {
          setBounds({ before: null, after: null })
        }
      })
      .finally(() => {
        if (requestKeyRef.current === requestKey) setProbing(false)
      })
  }, [
    sensorKey,
    rangeKey,
    sensorIds,
    timeRange.from,
    timeRange.to,
    fetchDataPresence,
  ])

  const jumpTo = useCallback(
    (boundaryIso: string) => {
      const spanMs = Date.parse(timeRange.to) - Date.parse(timeRange.from)
      const window = boundaryJumpWindow(
        Date.parse(boundaryIso),
        spanMs,
        Date.now()
      )
      exitLiveTail()
      setTimeRange((prev) => ({ ...prev, from: window.from, to: window.to }))
    },
    [timeRange.from, timeRange.to, setTimeRange, exitLiveTail]
  )

  const { before, after } = bounds
  const hasEither = before !== null || after !== null

  return (
    <>
      <Title>
        <Trans>No data in this range</Trans>
      </Title>
      <Detail>
        {probing && !hasEither ? (
          t`Looking for the nearest data…`
        ) : hasEither ? (
          <>
            <Trans>The selected pens have data</Trans>
            {before !== null && <> before {formatDay(before)}</>}
            {before !== null && after !== null && ' and'}
            {after !== null && <> after {formatDay(after)}</>}.
          </>
        ) : (
          t`No data found near this range for the selected pens.`
        )}
      </Detail>
      {hasEither && (
        <Actions>
          {before !== null && (
            <JumpButton type='button' onClick={() => jumpTo(before)}>
              <Trans>&larr; Jump to earlier data</Trans>
            </JumpButton>
          )}
          {after !== null && (
            <JumpButton type='button' onClick={() => jumpTo(after)}>
              <Trans>Jump to later data &rarr;</Trans>
            </JumpButton>
          )}
        </Actions>
      )}
    </>
  )
}
